import { useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';
import { LIST_MEDIA_AGENT_BOOKINGS_QUERY_KEY } from '@/state/queries/media-agent-admin/use-list-media-agent-bookings';
import { LIST_MEDIA_AGENT_POOL_QUERY_KEY } from '@/state/queries/media-agent-admin/use-list-media-agent-pool';
import { LIST_PROCESSOR_POOL_QUERY_KEY } from '@/state/queries/processor-admin/use-list-processor-pool';
import { LIST_SINGLECAM_AGENT_POOL_QUERY_KEY } from '@/state/queries/singlecam-agent-admin/use-list-singlecam-agent-pool';
import { ATTENTION_QUEUE_QUERY_KEY } from '@/state/queries/ops-admin/use-attention-queue';
import { PLATFORM_SUMMARY_QUERY_KEY } from '@/state/queries/ops-admin/use-platform-summary';

type AgentPoolLiveDomain = 'processor' | 'singlecam' | 'media-agent' | 'ops';

type AgentPoolsInvalidatedPayload = {
  domains: AgentPoolLiveDomain[];
  updatedAt: string;
};

function invalidateDomains(queryClient: ReturnType<typeof useQueryClient>, domains: AgentPoolLiveDomain[]) {
  const unique = new Set(domains);

  if (unique.has('media-agent')) {
    void queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_AGENT_POOL_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_AGENT_BOOKINGS_QUERY_KEY] });
  }
  if (unique.has('processor')) {
    void queryClient.invalidateQueries({ queryKey: [LIST_PROCESSOR_POOL_QUERY_KEY] });
  }
  if (unique.has('singlecam')) {
    void queryClient.invalidateQueries({ queryKey: [LIST_SINGLECAM_AGENT_POOL_QUERY_KEY] });
  }
  if (unique.has('ops')) {
    void queryClient.invalidateQueries({ queryKey: [PLATFORM_SUMMARY_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [ATTENTION_QUEUE_QUERY_KEY] });
  }
}

/**
 * Subscribes to ops-admin websocket pushes and invalidates pool/ops React Query caches.
 * Use alongside {@link AGENT_POOLS_LIVE_REFETCH_MS} polling as a fallback.
 */
export function useAgentPoolsLiveSync(enabled = true) {
  const { getToken, isSignedIn } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !isSignedIn) {
      return;
    }

    let socket: Socket | null = null;
    let cancelled = false;

    void (async () => {
      const token = await getToken();
      if (!token || cancelled) {
        return;
      }

      socket = io(`${import.meta.env.VITE_WS_URL}/ops-admin`, {
        autoConnect: true,
        auth: { token },
      });

      socket.on('agent-pools-invalidated', (payload: AgentPoolsInvalidatedPayload) => {
        if (!payload?.domains?.length) {
          return;
        }
        invalidateDomains(queryClient, payload.domains);
      });
    })();

    return () => {
      cancelled = true;
      socket?.disconnect();
      socket = null;
    };
  }, [enabled, getToken, isSignedIn, queryClient]);
}
